import { useInfiniteQuery } from '@tanstack/react-query';
import { catalogSearchService } from '../app/services/catalog-search.service';

interface CatalogSearchOptions {
  catalog: string;
  search?: string;
  fields?: string[];
  enabled?: boolean;
  selectedId?: string | number;
  extraParams?: Record<string, any>;
}

export interface CatalogItem {
  id: number | string;
  name?: string;
  title?: string;
  description?: string;
  code?: string;
  [key: string]: any;
}

export interface PaginatedResponse {
  data: CatalogItem[];
  current_page: number;
  last_page: number;
  total: number;
  [key: string]: any;
}

/**
 * Hook reutilizable para buscar catálogos remotos usando Infinite Query de TanStack Query.
 * Soporta paginación de Laravel.
 */
export function useCatalogSearch({ catalog, search = '', fields = [], enabled = true, extraParams }: CatalogSearchOptions) {
  return useInfiniteQuery({
    queryKey: ['catalogSearch', catalog, search, fields, extraParams],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await catalogSearchService.search(catalog, search, pageParam, fields, extraParams);

      // Adaptar a diferentes estructuras de respuesta posibles
      if (response?.data && Array.isArray(response.data)) {
        return response as PaginatedResponse;
      } else if (Array.isArray(response)) {
        // En caso de que no venga paginado se simula
        return {
          data: response,
          current_page: 1,
          last_page: 1,
          total: response.length
        } as PaginatedResponse;
      }

      return { data: [], current_page: 1, last_page: 1, total: 0 } as PaginatedResponse;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.current_page || !lastPage.last_page) return undefined;
      return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined;
    },
    enabled: !!catalog && enabled,
    staleTime: 1000 * 60 * 5, // 5 minutos de caché para evitar llamadas innecesarias
  });
}
